<template>
  <div>
    <article class="mb-5 d-flex flex-wrap">
      <div class="mb-4 icon-outer mr-3 text-center">
        <transition name="fade" mode="in-out">
          <Loader v-if="!fileLink && !errorHappened" key="icon-loader" />
          <div v-else-if="fileLink" key="icon-done">
            <span class="done-icon"></span>
          </div>
          <div v-else key="icon-error">
            <span class="error-icon"></span>
          </div>
        </transition>
      </div>
      <div v-if="!fileLink && !errorHappened">
        <h4>Exporting the data...</h4>
        <p>
          <b class="d-block">Info:</b>
          {{ $t('longLoadingText') }}
        </p>
      </div>
      <div v-else-if="fileLink">
        <h4>Thank you for using Tilisy</h4>
        <p class="mb-0">The data was successfully exported. Downloading will start automatically.</p>
        <p>
          <span class="mr-2">If downloading did not start, please click here:</span>
          <a :href="fileLink" target="_blank" class="btn btn-outline-theme btn-sm">Download the file</a>
        </p>
      </div>
      <div v-else-if="errorHappened">
        <h4>Data export has failed</h4>
        <p
          class="mb-0"
        >An error occurred during the export process. We truly apologise for the problem.</p>
        <p>
          For refunding your payment please contact us at
          <a
            :href="`mailto:${contactEmail}`"
          >{{ contactEmail }}</a>
        </p>
      </div>
    </article>
    <SectionTitle>
      <span class="text-theme">5.</span> Close session and wipe out data
    </SectionTitle>
    <button class="btn btn-theme" v-on:click="wipeAndClose">Close session</button>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import SectionTitle from '../components/elements/SectionTitle'
import { REQUEST_INTERVAL, REQUESTS_AMOUNT_LIMIT, CONTACT_EMAIL, exportStatuses } from '../constants'

export default {
  data() {
    return {
      fileLink: '',
      errorHappened: false,
      requestsAmount: 0,
      contactEmail: CONTACT_EMAIL
    }
  },
  components: {
    SectionTitle
  },
  computed: {
    ...mapState('payment', ['loading'])
  },
  async mounted() {
    this.getFile()
  },
  beforeDestroy() {
    clearTimeout(this.sessionCheckTimeout)
  },
  methods: {
    ...mapActions('personal', ['wipeAllData', 'exportStatusRequest', 'exportDataRequest']),
    wipeAndClose() {
      this.$router.push({ name: 'home' })
    },
    async getFile() {
      const response = await this.exportStatusRequest()

      if (response && response.status === exportStatuses.Success) {
        this.fileLink = response.url
        this.download(this.fileLink)
      } else if (response && response.status === exportStatuses.Failed) {
        this.errorHappened = true
      } else if (response && response.status === exportStatuses.NotStarted) {
        await this.exportDataRequest()
        this.sessionCheckTimeout = setTimeout(() => {
          this.getFile()
        }, REQUEST_INTERVAL)
      } else if (response && response.status === exportStatuses.Retry) {
        await this.exportDataRequest()
        this.sessionCheckTimeout = setTimeout(() => {
          this.getFile()
        }, REQUEST_INTERVAL)
      } else {
        this.sessionCheckTimeout = setTimeout(() => {
          this.getFile()
        }, REQUEST_INTERVAL)
      }
      this.requestsAmount++
      if (this.requestsAmount > REQUESTS_AMOUNT_LIMIT) {
        clearTimeout(this.sessionCheckTimeout)
      }
    },
    download(file) {
      const a = document.createElement('a')

      document.body.appendChild(a)
      a.style = 'display: none'
      a.href = file
      a.target = '_blank'
      a.click()
      document.body.removeChild(a)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../style/variables';

$icon-size: 5rem;
$bold: $icon-size/14;

.icon-outer {
  width: $icon-size;
  height: $icon-size;
  position: relative;
  .loader {
    width: inherit;
    height: inherit;
  }
}

.circled-icon {
  position: absolute;
  z-index: 1;
  opacity: 0;
  border-radius: 50%;
  width: $icon-size;
  min-width: $icon-size;
  height: $icon-size;
  position: relative;
  display: inline-block;
  animation: 0.2s ease-out done-anim 1 forwards;
  animation-delay: 0.5s;
  overflow: hidden;

  @keyframes done-anim {
    0% {
      opacity: 1;
      transform: rotate(-100deg) scale(0.6);
      border-color: $theme;
    }
    100% {
      opacity: 1;
      transform: rotate(0) scale(1);
    }
  }
}

.done-icon {
  @extend .circled-icon;
  border: $bold solid $gray-400;
  &:after {
    content: '';
    position: absolute;
    left: calc(50% - #{$icon-size/6});
    top: calc(50% - #{$icon-size/2.4});
    width: $icon-size/3;
    height: $icon-size/2;
    box-shadow: $bold $bold 0 0 $gray-400;
    transform: rotate(45deg);
  }
}
.error-icon {
  @extend .circled-icon;
  box-shadow: inset 0 0 0 5px darken($danger, 20%);
  border: $bold solid $danger;
  &:after,
  &:before {
    content: '';
    transform-origin: $bold/6 $bold/6;
    width: $icon-size * 1.2;
    height: $bold;
    position: absolute;
    left: 0;
    top: -$bold/2;
    z-index: 0;
    background-color: $danger;
    transform: rotate(45deg);
  }
  &:before {
    left: -$bold/2;
    top: calc(100% - #{$bold}/ 2);
    transform: rotate(-45deg);
  }
}
.fade-enter-active {
  transition: opacity 0.2s ease;
  opacity: 1;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
